// src/components/MeasureStatusRow.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dropdown, Form, Spinner } from 'react-bootstrap';
import { IconPencil, IconLock, IconChevronDown } from '@tabler/icons-react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMeasureById, saveMeasure } from '../features/measuresSlice';
import { updateMeasureStatus } from '../features/checkpointsSlice';
import MeasureOffcanvas from './offcanvas/MeasureOffcanvas';
import './MeasureStatusRow.css';

// Default status key for null/empty values
const DEFAULT_STATUS = 'kein-status';

// Status configuration for dropdown options and colors
const STATUS_OPTIONS = {
  [DEFAULT_STATUS]: { label: 'Kein Status', barClass: 'status-no-status' },
  'gestoppt': { label: 'gestoppt', barClass: 'status-stopped' },
  'nicht begonnen': { label: 'nicht begonnen', barClass: 'status-not-started' },
  'in Bearbeitung - verzögert': { label: 'in Bearbeitung - verzögert', barClass: 'status-in-progress-delayed' },
  'in Bearbeitung - nach Plan': { label: 'in Bearbeitung - nach Plan', barClass: 'status-in-progress-on-time' },
  'abgeschlossen': { label: 'abgeschlossen', barClass: 'status-completed' },
};

// Helper to normalize status (null/empty -> DEFAULT_STATUS)
const normalizeStatus = (status) => {
  return status && status.trim() !== '' ? status : DEFAULT_STATUS;
};

const getStatusBarClass = (status) => {
  const normalized = normalizeStatus(status);
  return STATUS_OPTIONS[normalized]?.barClass || 'status-no-status';
};

const getStatusDropdownClass = (status) => {
  const normalized = normalizeStatus(status);
  return STATUS_OPTIONS[normalized]?.barClass || 'status-no-status';
};

// Single Measure Item with integrated status
const MeasureStatusItem = ({
  measure,
  actionAreaId,
  unitId,
  checkpointId,
  checkpointIndex,
  actionAreaIndex,
  measureIndex,
  readOnly,
  isLast = false,
}) => {
  const dispatch = useDispatch();
  const descriptionTimer = useRef(null);
  
  // Get measure data from Redux store
  const measureData = useSelector((state) => state.measures.entities[measure.id]);
  
  // Local states
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [localTitle, setLocalTitle] = useState('');
  const [currentStatus, setCurrentStatus] = useState(normalizeStatus(measure.status));
  const [description, setDescription] = useState(measure.status_description || '');
  
  // Fetch measure data if not available
  useEffect(() => {
    if (!measureData && unitId) {
      dispatch(fetchMeasureById({ actionAreaId, measureId: measure.id }));
    }
  }, [measureData, dispatch, actionAreaId, measure.id, unitId]);
  
  // Update local title when measure data changes
  useEffect(() => {
    if (measureData) {
      setLocalTitle(measureData.measure_title || '');
    }
  }, [measureData]);
  
  // Update status states when measure prop changes
  useEffect(() => {
    setCurrentStatus(normalizeStatus(measure.status));
    setDescription(measure.status_description || '');
  }, [measure.status, measure.status_description]);

  const flushDescriptionSave = useCallback(() => {
    if (readOnly || description === (measure.status_description || '')) {
      return;
    }

    dispatch(
      updateMeasureStatus({
        unitId,
        checkpointId,
        statusId: measure.status_id,
        newStatus: currentStatus,
        newStatusDescription: description,
      })
    );
  }, [
    checkpointId,
    currentStatus,
    description,
    dispatch,
    measure.status_description,
    measure.status_id,
    readOnly,
    unitId,
  ]);
  
  // Debounced description save
  useEffect(() => {
    if (descriptionTimer.current) {
      clearTimeout(descriptionTimer.current);
      descriptionTimer.current = null;
    }
    
    if (!readOnly && description !== (measure.status_description || '')) {
      descriptionTimer.current = setTimeout(() => {
        flushDescriptionSave();
        descriptionTimer.current = null;
      }, 500);
    }
    
    return () => {
      if (descriptionTimer.current) {
        clearTimeout(descriptionTimer.current);
        descriptionTimer.current = null;
      }
    };
  }, [description, flushDescriptionSave, measure.status_description, readOnly]);

  useEffect(() => {
    return () => {
      if (descriptionTimer.current) {
        clearTimeout(descriptionTimer.current);
        descriptionTimer.current = null;
      }

      flushDescriptionSave();
    };
  }, [flushDescriptionSave]);
  
  // Handle title save
  const saveTitle = () => {
    if (measureData && localTitle !== measureData.measure_title) {
      const updatedMeasure = { ...measureData, measure_title: localTitle };
      dispatch(saveMeasure({ actionAreaId, measure: updatedMeasure }));
    }
  };
  
  // Handle status change
  const handleStatusSelect = (selectedStatus) => {
    if (readOnly) return;
    
    setCurrentStatus(selectedStatus);
    // Convert DEFAULT_STATUS back to null for backend
    const statusForBackend = selectedStatus === DEFAULT_STATUS ? null : selectedStatus;
    dispatch(
      updateMeasureStatus({
        unitId,
        checkpointId,
        statusId: measure.status_id,
        newStatus: statusForBackend,
        newStatusDescription: description,
      })
    );
  };
  
  // Handle description change
  const handleDescriptionChange = (e) => {
    if (readOnly) return;
    setDescription(e.target.value);
  };

  const handleDescriptionBlur = () => {
    if (descriptionTimer.current) {
      clearTimeout(descriptionTimer.current);
      descriptionTimer.current = null;
    }

    flushDescriptionSave();
  };
  
  // Handle opening offcanvas
  const handleOpenDetails = () => {
    saveTitle();
    setShowOffcanvas(true);
  };
  
  // Handle closing offcanvas
  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false);
  };
  
  if (!measureData) {
    return (
      <div className="measure-status-loading">
        <Spinner animation="border" size="sm" />
        <span>Lädt...</span>
      </div>
    );
  }
  
  return (
    <div className="measure-status-item">
      <div className="measure-status-item-inner">
        {/* Left status bar */}
        <div className={`measure-status-bar ${getStatusBarClass(currentStatus)}`} />
        
        {/* Main content */}
        <div className="measure-status-content">
          {/* Header Row: Title + Status Dropdown */}
          <div className="measure-header">
            {/* Left side: Title as link with edit icon */}
            <button 
              className={`measure-title-btn ${readOnly ? 'readonly' : ''}`}
              onClick={!readOnly ? handleOpenDetails : undefined}
              type="button"
              disabled={readOnly}
            >
              <span className="aa-checkpoint-title-text">
                {localTitle || 'Maßnahme ohne Titel'}
              </span>
              {!readOnly && (
                <IconPencil size={16} stroke={2} className="measure-edit-icon" />
              )}
            </button>
            
            {/* Right side: Status dropdown or read-only indicator */}
            <div className="measure-status-section">
              {readOnly ? (
                <span className="measure-readonly-indicator">
                  <IconLock size={14} stroke={1.5} />
                  <span>Nur Lesezugriff</span>
                </span>
              ) : (
                <Dropdown
                  onSelect={handleStatusSelect}
                  className="measure-status-dropdown"
                >
                  <Dropdown.Toggle
                    id={`status-dropdown-${measure.id}`}
                    className={getStatusDropdownClass(currentStatus)}
                    disabled={readOnly}
                  >
                    <span>{STATUS_OPTIONS[currentStatus]?.label || 'Kein Status'}</span>
                    <IconChevronDown size={16} stroke={2} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {Object.entries(STATUS_OPTIONS).map(([key, { label }]) => (
                      <Dropdown.Item key={key} eventKey={key}>
                        {label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </div>
          
          {/* Status description textarea */}
          <div className="measure-description-section">
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Statusdetails eingeben"
              value={description}
              onChange={handleDescriptionChange}
              onBlur={handleDescriptionBlur}
              readOnly={readOnly}
              className="measure-description-input"
            />
          </div>
        </div>
      </div>
      
      {/* Measure Offcanvas */}
      <MeasureOffcanvas
        showOffcanvas={showOffcanvas}
        handleCloseOffcanvas={handleCloseOffcanvas}
        measureId={measure.id}
        actionAreaId={actionAreaId}
        unitId={unitId}
      />

      {!isLast && <hr />}
    </div>
  );
};

const MeasureStatusRow = ({
  checkpointIndex,
  actionAreaIndex,
  checkpointId,
  actionAreaId,
  unitId,
  measures,
  hideHeaders = false,
  readOnly = false,
}) => {
  
  if (!measures || measures.length === 0) {
    return (
      <div className="measure-status-empty">
        Keine Maßnahmen verfügbar.
      </div>
    );
  }
  
  return (
    <div className="measure-status-row-container">
      {measures.map((measure, measureIndex) => (
        <MeasureStatusItem
          key={measure.id}
          measure={measure}
          actionAreaId={actionAreaId}
          unitId={unitId}
          checkpointId={checkpointId}
          checkpointIndex={checkpointIndex}
          actionAreaIndex={actionAreaIndex}
          measureIndex={measureIndex}
          readOnly={readOnly}
          isLast={measureIndex === measures.length - 1}
        />
      ))}
    </div>
  );
};

export default MeasureStatusRow;
